// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngLoadingOverlay from "@keller/App/Components/Common/XngLoadingOverlay";
import XngUtilities from "@keller/App/Components/Common/XngUtilities";
import loadMaintenance from "@keller/App/Handlers/Maintenance/LoadMaintenance";
import xHeaderUpdate from "@keller/App/Handlers/XNG/Header";
import useSize from "@keller/App/Hooks/useSize";
import { MaintenanceModel } from "@keller/App/Services/MaintenanceService";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { LogoLinksSearchStyles } from "@keller/Overrides/Widgets/Header/LogoLinksSearch";
import { constants, DivWrapper } from "@keller/XDL/constants";
import { buttonTransparent } from "@keller/XDL/PreStyle/button";
import { color, hideElementOnPrint, layoutContainer, xScreen } from "@keller/XDL/Utilities";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    isSmallMobileScreen: state.xCommon.isSmallMobileScreen,
    isTabletScreen: state.xCommon.isTabletScreen,
    registerMenu: state.xHeader.registerMenu,
    isSticky: state.xHeader.isSticky,
    currentLocation: getLocation(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    xHeaderUpdate,
    loadMaintenance: makeHandlerChainAwaitable<{}, MaintenanceModel>(loadMaintenance),
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    headerEmpty?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        css: css`
            background-color: ${getColor("common.white")};
            width: 100%;
            position: relative;
            z-index: ${getProp("theme.zIndex.header")};
            &&&& {
                ${xScreen("lte", "md")} {
                    position: fixed;
                    top: 0;
                    box-shadow: 0 1px 0 0 ${color("common.ui8", 0.25)};
                }
                ${xScreen("lte", 767)} {
                    box-shadow: none;
                }
            }
        `,
    },
    headerEmpty: {
        css: css`
            ${hideElementOnPrint}
            ${xScreen("gt", "md")} {
                display: none;
            }
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("primary")};
            display: flex;
            > div {
                ${layoutContainer}
            }

            ${xScreen("lte", "md")} {
                display: none;
            }

            ${xScreen("lte", 767)} {
                display: flex;
                &&& > div {
                    padding-top: 8px;
                    padding-bottom: 8px;
                    padding-right: 14px;
                    padding-left: 14px;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 18px;
            padding-bottom: 18px;

            ${xScreen("lte", "md")} {
                padding-top: 8px;
                padding-bottom: 4px;
            }

            ${xScreen("lte", 767)} {
                box-shadow: none;
            }

            ${layoutContainer}
        `,
    },
    actionsContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            flex-grow: 1;
            flex-wrap: wrap;
        `,
    },
    logoWrapper: {
        css: css`
            margin-right: 58px;
            img {
                height: 70px;
                width: 175px;
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            flex-basis: 100%;
            margin-bottom: 15px;
            & > ${DivWrapper} {
                display: flex;
                justify-content: space-between;
                align-items: center;
                & > div:not(:last-child) {
                    display: flex;
                    margin-right: 20px;
                }
            }
        `,
    },
    searchWrapper: {
        css: css`
            margin-right: 50px;
            flex-grow: 1;
            max-width: 695px;
            ${xScreen("lte", "md")} {
                max-width: 100%;
                margin-right: 0;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            & > ${DivWrapper} {
                display: flex;
                gap: 20px;
            }
        `,
    },
    mobile: {
        container: {
            css: css`
                width: 100%;
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: center;
                ${xScreen("lte", "md")} {
                    gap: 0 34px;
                }
                ${xScreen("lte", 767)} {
                    gap: initial;
                }
            `,
        },
        logoMenu: {
            css: css`
                display: flex;
                align-items: center;
                gap: 29px;
                img {
                    max-height: 70px;
                    width: 98px;
                }
            `,
        },
        searchMenuWrapper: {
            css: css`
                flex-basis: 100%;
                padding-bottom: 8px;
                margin-bottom: 8px;
                position: relative;
                &:before {
                    content: "";
                    background: ${color("common.border")};
                    height: 1px;
                    width: calc(100% + 40px);
                    position: absolute;
                    left: -20px;
                    top: 100%;
                }
            `,
        },
        searchWrapper: {
            css: css`
                display: flex;
                flex-basis: 100%;
                justify-content: center;
                background: ${color("primary")};
                margin-top: 40px;
            `,
        },
        menuTriggerButton: {
            css: css`
                margin-right: 10px;
                padding: 0;
                ${IconWrapper} {
                    color: ${color("primary")};
                }
                ${buttonTransparent}
            `,
        },
        actionsWrapper: {
            css: css`
                & > ${DivWrapper} {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 4px;
                }
            `,
        },
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isTabletScreen,
    registerMenu,
    currentLocation,
    isSticky,
    xHeaderUpdate,
    loadMaintenance,
    isSmallMobileScreen,
}: Props) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;
    const [isMaintenance, setIsMaintenance] = useState(false);

    // XNG-Changes
    const transitionElement = React.useRef(null);

    const checkIfMaintenanceChange = async () => {
        const result = await loadMaintenance({});
        if (isMaintenance !== result.isDown) {
            window.location.reload();
        }
    };

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    const isMaintenanceChangeRef = useRef(checkIfMaintenanceChange);
    isMaintenanceChangeRef.current = checkIfMaintenanceChange;

    // XNG-Changes
    const maintenance = async () => {
        const result = await loadMaintenance({});
        setIsMaintenance(result?.isDown);
        const delay = parseInt(`${result?.downCacheMinutes || "5"}`, 10) * 60000;
        setInterval(() => {
            isMaintenanceChangeRef.current();
        }, delay);
    };

    useEffect(() => {
        maintenance();
    }, []);

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        if (headerHeight) {
            xHeaderUpdate({ key: "headerHeight", value: headerHeight });
        }
    }, [headerHeight]);

    const afterHeader = React.createRef<HTMLSpanElement>();

    // XNG-Changes: structure
    return (
        <>
            <StyledWrapper
                className="header-emptyDiv"
                {...styles.headerEmpty}
                style={{ height: `${headerHeight || 0}px` }}
            />
            <StyledWrapper {...styles.websiteName}>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <StyledWrapper as="header" isHeader={true} ref={headerRef} {...styles.page} id="header-page">
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <Zone contentId={id} zoneName="MaintenanceMessage" />
                    <StyledWrapper
                        {...logoLinksSearchStyles.container}
                        data-test-selector="logoLinksSearchStylesContainer"
                    >
                        {isTabletScreen ? (
                            <StyledWrapper
                                {...logoLinksSearchStyles.mobile?.container}
                                data-test-selector="logoLinksSearchStylesMobileContainer"
                            >
                                <StyledWrapper {...logoLinksSearchStyles.mobile?.searchMenuWrapper}>
                                    <Zone contentId={id} zoneName="LinkList" />
                                </StyledWrapper>
                                <StyledWrapper {...logoLinksSearchStyles.mobile?.logoMenu}>
                                    {!isSmallMobileScreen && (
                                        <Zone contentId={id} zoneName="MainNavigation" normalWidth />
                                    )}
                                    <Zone contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                {!isSmallMobileScreen && (
                                    <StyledWrapper {...logoLinksSearchStyles.searchWrapper}>
                                        <Zone contentId={id} zoneName="Search" />
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...logoLinksSearchStyles.mobile?.actionsWrapper}>
                                    <Zone contentId={id} zoneName="Actions" />
                                </StyledWrapper>
                            </StyledWrapper>
                        ) : (
                            <>
                                <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                                    <Zone contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                <StyledWrapper {...logoLinksSearchStyles.actionsContainer}>
                                    <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                                        <Zone contentId={id} zoneName="LinkList" />
                                    </StyledWrapper>
                                    <StyledWrapper {...logoLinksSearchStyles.searchWrapper}>
                                        <Zone contentId={id} zoneName="Search" />
                                    </StyledWrapper>
                                    <StyledWrapper {...logoLinksSearchStyles.actionsWrapper}>
                                        <Zone contentId={id} zoneName="Actions" />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.container}>
                        {isSmallMobileScreen ? (
                            <StyledWrapper>
                                <Zone contentId={id} zoneName="MainNavigation" normalWidth />
                                <Zone contentId={id} zoneName="Search" />
                            </StyledWrapper>
                        ) : (
                            <Zone contentId={id} zoneName="MainNavigation" />
                        )}
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <XngUtilities />
                <XngLoadingOverlay />
                <span ref={afterHeader} tabIndex={-1} />
            </StyledWrapper>
            {/* XNG-Changes */}
            <Transition
                mountOnEnter
                unmountOnExit
                in={registerMenu && registerMenu.length > 0}
                timeout={{ enter: 75, exit: 250 }}
                nodeRef={transitionElement}
            >
                {state => (
                    <StyledOverlay
                        ref={transitionElement}
                        className={registerMenu && isTabletScreen ? "header-overlay" : ""}
                        transitionState={state}
                    />
                )}
            </Transition>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;

const StyledOverlay = styled.div<{ transitionState: TransitionStatus } & ThemeProps<BaseTheme>>`
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: ${({ theme }: any) => {
        return theme.zIndex.styledOverlay;
    }};
    transition: opacity ease-in-out 250ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
`;
